import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { voiceprintClient } from "@voiceprint/voiceprintClient";

const voiceprintKeys = {
  all: ["voiceprint"] as const,
  status: (sessionKey: string) => ["voiceprint", sessionKey] as const,
};

const ENROLLING_POLL_MS = 3_000;

/** Polls only while `enrolling` and pending, not for every unenrolled visit to the page. */
export const useVoiceprintStatus = (sessionKey: string, enrolling: boolean) =>
  useQuery({
    queryKey: voiceprintKeys.status(sessionKey),
    queryFn: voiceprintClient.getStatus,
    refetchInterval: (query) =>
      enrolling && query.state.data?.status === "pending"
        ? ENROLLING_POLL_MS
        : false,
  });

/** Enrollment failures render inline, so the global write-error notice stays quiet. */
export const useUploadVoiceprintSample = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file, sittingKey }: { file: Blob; sittingKey: string }) =>
      voiceprintClient.uploadEnrollment(file, sittingKey),
    gcTime: 0,
    meta: { handled: true },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: voiceprintKeys.all }),
  });
};

export const useDeleteVoiceprint = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => voiceprintClient.delete(),
    meta: { action: "delete your voiceprint" },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: voiceprintKeys.all }),
  });
};
